import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Trash2, Copy } from "lucide-react";
import api from "../lib/api";
import { useFlash } from "../lib/flash";
import { apiError, useAuth } from "../lib/auth";
import { useGroupStores } from "./StoreSwitcher";

function useEsc(onCancel) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onCancel]);
}

function DeleteDialog({ title, count, busy, onCancel, onConfirm }) {
  useEsc(onCancel);
  return createPortal(
    <div className="ck-overlay" role="dialog" aria-modal="true" onClick={onCancel} data-testid="offer-delete-dialog">
      <div className="ck-modal od-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ck-body">
          <h2 className="od-title">Slet tilbuddet?</h2>
          <p className="od-name">«{title}»</p>
          <p data-testid="offer-delete-text">
            Opslaget bliver liggende på Facebook, men nye "ja tak" registreres ikke.{" "}
            <b>{count} {count === 1 ? "eksisterende reservation bevares" : "eksisterende reservationer bevares"}</b> og kan stadig udleveres.
          </p>
          <div className="od-actions">
            <button type="button" className="btn" onClick={onCancel} data-testid="offer-delete-cancel">Annuller</button>
            <button type="button" className="btn danger-solid" onClick={onConfirm} disabled={busy} data-testid="offer-delete-confirm">
              {busy ? "Sletter…" : "Slet tilbud"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CopyDialog({ title, stores, busy, onCancel, onConfirm }) {
  useEsc(onCancel);
  const [picked, setPicked] = useState(stores.filter((s) => s.current).map((s) => s.id));
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return createPortal(
    <div className="ck-overlay" role="dialog" aria-modal="true" onClick={onCancel} data-testid="offer-copy-dialog">
      <div className="ck-modal od-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ck-body">
          <h2 className="od-title">Dupliker tilbuddet</h2>
          <p className="od-name">«{title}»</p>
          <p>Kopien bliver en kladde – ingen reservationer eller Facebook-opslag kopieres.</p>
          <fieldset className="oc-stores">
            <legend>Kopier til</legend>
            {stores.map((s) => (
              <label key={s.id} className="oc-store" data-testid={`offer-copy-store-${s.id}`}>
                <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} />
                <span>{s.name}{s.current && <em> (denne butik)</em>}</span>
              </label>
            ))}
          </fieldset>
          <div className="od-actions">
            <button type="button" className="btn" onClick={onCancel} data-testid="offer-copy-cancel">Annuller</button>
            <button type="button" className="btn primary" onClick={() => onConfirm(picked)} disabled={busy || !picked.length} data-testid="offer-copy-confirm">
              {busy ? "Kopierer…" : "Dupliker"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function OfferMenu({ offer, count, onChange, onlyDuplicate }) {
  const flash = useFlash();
  const nav = useNavigate();
  const { store, switchStore } = useAuth();
  const group = useGroupStores();
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const fail = (err) => flash(apiError(err.response?.data?.detail) || err.message, "error");
  const undo = async () => {
    try {
      await api.post(`/offers/${offer.id}/restore`);
      flash("Tilbuddet er gendannet.");
      onChange?.();
    } catch (err) {
      fail(err);
    }
  };
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/offers/${offer.id}`);
      setAsk(null);
      flash(`Tilbuddet «${offer.title}» er slettet.`, "", { action: "Fortryd", onAction: undo, ms: 10000 });
      onChange?.();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const copy = async (ids) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offer.id}/copy`, { store_ids: ids });
      setAsk(null);
      const here = data.copies.find((c) => c.store_id === store.id);
      const other = data.copies.filter((c) => c.store_id !== store.id);
      if (other.length)
        flash(`Kopieret til ${other.map((c) => c.store_name).join(", ")}`, "", {
          action: "Skift butik", onAction: () => switchStore(other[0].store_id), ms: 10000,
        });
      if (here) {
        flash("Kopi oprettet som kladde");
        nav(`/offers/${here.id}/edit`);
      } else onChange?.();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const startCopy = () => (group?.stores?.length > 1 ? setAsk("copy") : copy([store.id]));

  return (
    <span className="om-wrap" ref={ref} onClick={stop}>
      <button type="button" className="om-btn" aria-label="Flere handlinger" aria-expanded={open}
        onClick={(e) => { stop(e); setOpen((v) => !v); }} data-testid={`offer-menu-${offer.id}`}>
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <div className="om-pop" role="menu" data-testid={`offer-menu-pop-${offer.id}`}>
          <button type="button" role="menuitem" className="om-item" disabled={busy}
            onClick={(e) => { stop(e); setOpen(false); startCopy(); }} data-testid={`offer-duplicate-${offer.id}`}>
            <Copy size={16} /> Dupliker
          </button>
          {!onlyDuplicate && (
            <button type="button" role="menuitem" className="om-item danger"
              onClick={(e) => { stop(e); setOpen(false); setAsk("delete"); }} data-testid={`offer-delete-${offer.id}`}>
              <Trash2 size={16} /> Slet
            </button>
          )}
        </div>
      )}
      {ask === "delete" && <DeleteDialog title={offer.title} count={count || 0} busy={busy} onCancel={() => setAsk(null)} onConfirm={del} />}
      {ask === "copy" && <CopyDialog title={offer.title} stores={group.stores} busy={busy} onCancel={() => setAsk(null)} onConfirm={copy} />}
    </span>
  );
}
